import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  useMediaQuery,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { bgcolor, padding, styled } from '@mui/system';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { HOST_URL } from '../utils/Url';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import ToastApp from '../ToastApp';
import { ClipLoader } from 'react-spinners';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import "./index.css";
import LoginImg from '../assets/images/data.png';
import LogoImg from '../assets/images/recyclebin.png';
import CompanyLogo from '../assets/images/CompanyLogo.png.jpg'

// Original gradient colors
const GradientBackground = styled(Box)({
  background: 'linear-gradient(to right,#3342c2, #0b156a)',
  width: '100%',
  minHeight: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  padding: '2rem 0',
  borderRadius: "4rem",
});

const Container = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  maxWidth: 800,
  width: '95%',
  borderRadius: 20,
  overflow: 'hidden',
  boxShadow: '0 8px 30px rgb(0, 0, 0.3)',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

// Original left section styling
const LeftSection = styled(Box)(({ theme }) => ({
  flex: '0 0 55%',
  background: '#3246eb',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  padding: theme.spacing(4),
  borderRight: '10px solid rgb(200, 206, 255)',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 auto',
    padding: theme.spacing(6, 4),
    width: '100%',
    borderRight: 'none'
  },
}));

const RightSection = styled(Box)(({ theme }) => ({
  flex: '0 0 45%',
  backgroundColor: 'white',
  padding: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 auto',
    padding: theme.spacing(4),
    width: '100%',
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  width: '80%',
  maxWidth: 300,
  backgroundColor: "#fff",
  borderRadius: '50%',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  marginBottom: theme.spacing(4),
  '& img': {
    width: '100%',
    height: 'auto',
    borderRadius: '50%',
    // border: '8px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    padding: 3,
  },
  [theme.breakpoints.down('sm')]: {
    width: '60%',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 110,
  display: 'flex',
  justifyContent: 'center',
  marginTop: "6%",
  alignItems: 'center',
  '& img': {
    width: '100%',
    height: 'auto',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: 95,
  },
}));

// Original WelcomeBadge styling
const WelcomeBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  left: 0,
  background: '#3246eb',
  color: 'white',
  padding: '8px 24px',
  borderBottomRightRadius: 25,
  borderTopRightRadius: 25,
  fontWeight: 'bold',
  fontSize: '1.5rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '6px 16px',
  },
}));

const Footer = styled(Box)({
  marginTop: '1.5rem',
  textAlign: 'center',
  color: 'white',
  fontSize: '0.875rem',
  width: '100%',
});

const AnimatedText = styled(Typography)({
  opacity: 0,
  animation: 'fadeIn 1.5s ease-in-out forwards',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
});

const LoginPage: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const validationSchema = Yup.object({
    useR_ID: Yup.string()
      .required('Username is required')
      .min(3, 'Username must be at least 3 characters'),
    password: Yup.string().required('Password is required'),
  });

  const formik = useFormik({
    initialValues: {
      useR_ID: '',
      password: '',
      collageID: '1',
      packageID: '1',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await axios.post(
          HOST_URL + `USER/USERLOGIN`,
          values
        );
        if (response.data.isSuccess) {
          localStorage.setItem('userdata', JSON.stringify(response.data.data));
          localStorage.setItem(
            'useR_ID',
            JSON.stringify(response.data.data[0]['userdetail'][0]['useR_ID'])
          );
          sessionStorage.setItem(
            'token',
            JSON.stringify(response.data.data[0]['token'])
          );
          formik.resetForm();
          setIsInvalid(false);
          navigate('/splashscreen');
        } else {
          setIsInvalid(true);
          // toast.error(response.data.mesg);
        }
      } catch (error) {
        toast.error('An error occurred during login');
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div style={{ width: "100vw", height: "100vh", padding: "5vh 10vw", alignItems: "center", alignContent: "center" }}>
      <GradientBackground>
        <Container elevation={10}>
          <LeftSection>
            <ImageContainer>
              <img src={LoginImg} alt="Login Illustration" style={{
                animation: mounted ? 'zoomIn 1s ease-in-out forwards' : 'none'
              }} />
            </ImageContainer>
            <AnimatedText
              variant="h5"
              sx={{
                color: 'white',
                textAlign: 'center',
                mb: 2,
                fontWeight: 'bold',
                animationDelay: '0.2s'
              }}
            >
              File Tracking System
            </AnimatedText>
            <AnimatedText
              variant="body2"
              sx={{
                color: 'white',
                textAlign: 'center',
                mb: 2,
                fontWeight: 'bold',
                animationDelay: '0.4s'
              }}
            >
              Improves Efficiencies, Reduce Cost and Enhance Customer Service
            </AnimatedText>
          </LeftSection>

          <RightSection>
            <WelcomeBadge sx={{
              // opacity: 0,
              // animation: mounted ? 'slideIn 0.8s ease-out 0.2s forwards' : 'none',
              // '@keyframes slideIn': {
              //   '0%': { transform: 'translateX(-100%)', opacity: 0 },
              //   '100%': { transform: 'translateX(0)', opacity: 1 }
              // }
            }}>
              Welcome to Panchshala
            </WelcomeBadge>

            <LogoContainer sx={{
              opacity: 0,
              animation: mounted ? 'fadeIn 1s ease-in 0.2s forwards' : 'none'
            }}>
              <img src={LogoImg} alt="Company Logo" />
            </LogoContainer>

            <Box
              component="form"
              onSubmit={formik.handleSubmit}
              sx={{ width: '100%', justifyContent: "center", textAlign: "center" }}
            >
              <TextField
                label="Username"
                name="useR_ID"
                fullWidth
                margin="normal"
                variant="standard"
                placeholder="Enter your username"
                value={formik.values.useR_ID}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.useR_ID && Boolean(formik.errors.useR_ID)}
                helperText={formik.touched.useR_ID && formik.errors.useR_ID}
                sx={{
                  opacity: 0,
                  animation: mounted ? 'fadeIn 1s ease-in 0.4s forwards' : 'none',
                  '& label': {
                    color: '#0b156a',
                  },
                  '& label.Mui-focused': {
                    color: '#0b156a',
                  }
                }}
              />
              <TextField
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                variant="standard"
                placeholder="Enter your password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  opacity: 0,
                  animation: mounted ? 'fadeIn 1s ease-in 0.5s forwards' : 'none',
                  '& label': {
                    color: '#0b156a',
                  },
                  '& label.Mui-focused': {
                    color: '#0b156a',
                  }
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 3,
                  borderRadius: 5,
                  background: '#3246eb',
                  width: "160px",
                  height: 45,
                  fontWeight: 'bold',
                  opacity: 0,
                  animation: mounted ? 'fadeIn 1s ease-in 1s forwards' : 'none'
                }}
              >
                {loading ? <ClipLoader color="#ffffff" size={20} /> : 'LOGIN'}
              </Button>

              {isInvalid && (
                <div style={{ marginTop: "10px" }}>
                  <p style={{ padding: "5px", color: "red", backgroundColor: "#fee2e2", borderRadius: "5px" }}>Invalid Credentials. Login Failed !</p>
                </div>
              )}
            </Box>
          </RightSection>
        </Container>

        <Footer sx={{
          opacity: 0,
          animation: mounted ? 'fadeIn 1s ease-in 1s forwards' : 'none'
        }}>
          <div style={{ textAlign: "center" }}>
            <span style={{ padding: "0.4rem", backgroundColor: "#fff", borderRadius: "5px", color: "#000", fontSize: ".8rem" }}><b>
              Created by  <img src={CompanyLogo} alt='Company Logo' style={{ height: "auto", width: "1rem" }} /> MSSPL
            </b></span>
          </div>
        </Footer>

        <ToastApp />
      </GradientBackground>
    </div>
  );
};

export default LoginPage;




















// import React, { useState } from 'react';
// import {
//   Box,
//   Button,
//   TextField,
//   Typography,
//   Paper,
//   useMediaQuery,
//   InputAdornment,
//   IconButton,
// } from '@mui/material';
// import { bgcolor, padding, styled } from '@mui/system';
// import axios from 'axios';
// import { useNavigate } from 'react-router-dom';
// import { HOST_URL } from '../utils/Url';
// import { useFormik } from 'formik';
// import * as Yup from 'yup';
// import { toast } from 'react-toastify';
// import ToastApp from '../ToastApp';
// import { ClipLoader } from 'react-spinners';
// import { Visibility, VisibilityOff } from '@mui/icons-material';
// import "./index.css";
// import LoginImg from '../assets/images/data.png';
// import LogoImg from '../assets/images/recyclebin.png';
// import CompanyLogo from '../assets/images/CompanyLogo.png.jpg'

// // Original gradient colors
// const GradientBackground = styled(Box)({
//   background: 'linear-gradient(to right,#3342c2, #0b156a)',
//   width: '100vw',
//   minHeight: '100vh',
//   display: 'flex',
//   justifyContent: 'center',
//   alignItems: 'center',
//   flexDirection: 'column',
//   padding: '2rem 0',
// });

// const Container = styled(Paper)(({ theme }) => ({
//   display: 'flex',
//   flexDirection: 'row',
//   maxWidth: 800,
//   width: '95%',
//   borderRadius: 20,
//   overflow: 'hidden',
//   boxShadow: '0 8px 30px rgb(0, 0, 0.3)',
//   [theme.breakpoints.down('md')]: {
//     flexDirection: 'column',
//   },
// }));

// // Original left section styling
// const LeftSection = styled(Box)(({ theme }) => ({
//   flex: '0 0 55%',
//   background: '#3246eb',
//   color: 'white',
//   display: 'flex',
//   justifyContent: 'center',
//   alignItems: 'center',
//   flexDirection: 'column',
//   padding: theme.spacing(4),
//   borderRight: '10px solid rgb(200, 206, 255)',
//   [theme.breakpoints.down('md')]: {
//     flex: '0 0 auto',
//     padding: theme.spacing(6, 4),
//     width: '100%',
//     borderRight: 'none'
//   },
// }));

// const RightSection = styled(Box)(({ theme }) => ({
//   flex: '0 0 45%',
//   backgroundColor: 'white',
//   padding: theme.spacing(6),
//   display: 'flex',
//   flexDirection: 'column',
//   justifyContent: 'center',
//   alignItems: 'center',
//   position: 'relative',
//   [theme.breakpoints.down('md')]: {
//     flex: '0 0 auto',
//     padding: theme.spacing(4),
//     width: '100%',
//   },
// }));

// const ImageContainer = styled(Box)(({ theme }) => ({
//   width: '80%',
//   maxWidth: 300,
//   backgroundColor: "#fff",
//   borderRadius: '50%',
//   boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
//   marginBottom: theme.spacing(4),
//   '& img': {
//     width: '100%',
//     height: 'auto',
//     borderRadius: '50%',
//     // border: '8px solid rgba(255, 255, 255, 0.2)',
//     boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
//     padding: 3,
//   },
//   [theme.breakpoints.down('sm')]: {
//     width: '60%',
//   },
// }));

// const LogoContainer = styled(Box)(({ theme }) => ({
//   width: '100%',
//   maxWidth: 110,
//   display: 'flex',
//   justifyContent: 'center',
//   marginTop: "6%",
//   alignItems: 'center',
//   '& img': {
//     width: '100%',
//     height: 'auto',
//   },
//   [theme.breakpoints.down('sm')]: {
//     maxWidth: 95,
//   },
// }));

// // Original WelcomeBadge styling
// const WelcomeBadge = styled(Box)(({ theme }) => ({
//   position: 'absolute',
//   top: 10,
//   left: 0,
//   background: '#3246eb',
//   color: 'white',
//   padding: '8px 24px',
//   borderBottomRightRadius: 25,
//   borderTopRightRadius: 25,
//   fontWeight: 'bold',
//   fontSize: '1.5rem',
//   [theme.breakpoints.down('sm')]: {
//     fontSize: '1rem',
//     padding: '6px 16px',
//   },
// }));

// const Footer = styled(Box)({
//   marginTop: '1.5rem',
//   textAlign: 'center',
//   color: 'white',
//   fontSize: '0.875rem',
//   width: '100%',
// });

// const LoginPage: React.FC = () => {
//   const isMobile = useMediaQuery('(max-width:600px)');
//   const [loading, setLoading] = useState(false);
//   const [showPassword, setShowPassword] = useState(false);
//   const navigate = useNavigate();

//   const validationSchema = Yup.object({
//     useR_ID: Yup.string()
//       .required('Username is required')
//       .min(3, 'Username must be at least 3 characters'),
//     password: Yup.string().required('Password is required'),
//   });

//   const formik = useFormik({
//     initialValues: {
//       useR_ID: '',
//       password: '',
//       collageID: '1',
//       packageID: '1',
//     },
//     validationSchema: validationSchema,
//     onSubmit: async (values) => {
//       setLoading(true);
//       try {
//         const response = await axios.post(
//           HOST_URL + `USER/USERLOGIN`,
//           values
//         );
//         if (response.data.isSuccess) {
//           localStorage.setItem('userdata', JSON.stringify(response.data.data));
//           localStorage.setItem(
//             'useR_ID',
//             JSON.stringify(response.data.data[0]['userdetail'][0]['useR_ID'])
//           );
//           sessionStorage.setItem(
//             'token',
//             JSON.stringify(response.data.data[0]['token'])
//           );
//           formik.resetForm();
//           navigate('/splashscreen');
//         } else {
//           toast.error(response.data.mesg);
//         }
//       } catch (error) {
//         toast.error('An error occurred during login');
//       } finally {
//         setLoading(false);
//       }
//     },
//   });

//   const handleClickShowPassword = () => {
//     setShowPassword(!showPassword);
//   };

//   return (
//     <GradientBackground>
//       <Container elevation={10}>
//         <LeftSection>
//           <ImageContainer>
//             <img src={LoginImg} alt="Login Illustration" />
//           </ImageContainer>
//           <Typography
//             variant="h5"
//             sx={{
//               color: 'white',
//               textAlign: 'center',
//               mb: 2,
//               fontWeight: 'bold'
//             }}
//           >
//             File Tracking System
//           </Typography>
//           <Typography
//             variant="body2"
//             sx={{
//               color: 'white',
//               textAlign: 'center',
//               mb: 2,
//               fontWeight: 'bold'
//             }}
//           >
//             Improves Efficiencies, Reduce Cost and Enhance Customer Service
//           </Typography>

//         </LeftSection>

//         <RightSection>
//           <WelcomeBadge>Welcome to Panchshala</WelcomeBadge>

//           <LogoContainer>
//             <img src={LogoImg} alt="Company Logo" />
//           </LogoContainer>
//           {/* Welcome badge positioned as in original design */}

//           <Box
//             component="form"
//             onSubmit={formik.handleSubmit}
//             sx={{ width: '100%' }}
//           >
//             <TextField
//               label="Username"
//               name="useR_ID"
//               fullWidth
//               margin="normal"
//               variant="standard"
//               placeholder="Enter your username"
//               value={formik.values.useR_ID}
//               onChange={formik.handleChange}
//               onBlur={formik.handleBlur}
//               error={formik.touched.useR_ID && Boolean(formik.errors.useR_ID)}
//               helperText={formik.touched.useR_ID && formik.errors.useR_ID}
//             />
//             <TextField
//               label="Password"
//               name="password"
//               type={showPassword ? 'text' : 'password'}
//               fullWidth
//               margin="normal"
//               variant="standard"
//               placeholder="Enter your password"
//               value={formik.values.password}
//               onChange={formik.handleChange}
//               onBlur={formik.handleBlur}
//               error={
//                 formik.touched.password && Boolean(formik.errors.password)
//               }
//               helperText={formik.touched.password && formik.errors.password}
//               InputProps={{
//                 endAdornment: (
//                   <InputAdornment position="end">
//                     <IconButton
//                       aria-label="toggle password visibility"
//                       onClick={handleClickShowPassword}
//                       edge="end"
//                     >
//                       {showPassword ? <VisibilityOff /> : <Visibility />}
//                     </IconButton>
//                   </InputAdornment>
//                 ),
//               }}
//             />
//             <Button
//               type="submit"
//               variant="contained"
//               fullWidth
//               disabled={loading}
//               sx={{
//                 mt: 3,
//                 borderRadius: 5,
//                 background: '#3246eb',
//                 height: 45,
//                 fontWeight: 'bold'
//               }}
//             >
//               {loading ? <ClipLoader color="#ffffff" size={20} /> : 'LOGIN'}
//             </Button>
//           </Box>
//         </RightSection>
//       </Container>

//       <Footer>
//         <div style={{ textAlign: "center" }}>
//           <span style={{ padding: "0.2rem", backgroundColor: "#fff", borderRadius: "5px", color: "#000", fontSize: ".8rem" }}><b>
//             Created by  <img src={CompanyLogo} alt='Company Logo' style={{ height: "auto", width: "1rem" }} /> MSSPL
//           </b></span>
//         </div>
//       </Footer>

//       <ToastApp />
//     </GradientBackground>
//   );
// };

// export default LoginPage;
